import { FC, useState } from "react";
import {
  Modal,
  Button,
  SpaceBetween,
  FormField,
  Input,
} from "@cloudscape-design/components";
import { useAuthContext } from "../contexts/AuthContext";
import { prefixCollection } from "../utils/prefix";
import { createItem } from "../api/generic";
import { Customer } from "../models/Sales";
import { v4 as uuidv4 } from "uuid";

interface AddCustomerModalProps {
  visible: boolean;
  onClose: () => void;
  onCustomerAdded?: () => void;
}

const AddCustomerModal: FC<AddCustomerModalProps> = ({
  visible,
  onClose,
  onCustomerAdded,
}) => {
  const { tenant } = useAuthContext();

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("Nederland");

  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name) return; // Require name

    setSaving(true);

    const newCustomer: Customer = {
      id: uuidv4(),
      name,
      address,
      zipCode,
      city,
      country,
      contactName,
      contactEmail,
      contactPhone,
      createdAt: new Date(),
    };

    await createItem<Customer>(
      prefixCollection("customers", tenant),
      newCustomer
    );

    setSaving(false);
    onClose();
    if (onCustomerAdded) onCustomerAdded();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onClose}
      header="Add New Customer"
      footer={
        <div style={{ float: "right" }}>
          <SpaceBetween size="m" direction="horizontal">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!name.trim()}
            >
              Save
            </Button>
          </SpaceBetween>
        </div>
      }
    >
      <SpaceBetween size="l" direction="vertical">
        <FormField label="Customer Name">
          <Input
            value={name}
            onChange={({ detail }) => setName(detail.value)}
            placeholder="Enter customer name"
          />
        </FormField>

        <FormField label="Address">
          <Input
            value={address}
            onChange={({ detail }) => setAddress(detail.value)}
            placeholder="Street and house number"
          />
        </FormField>

        <FormField label="Zip Code">
          <Input
            value={zipCode}
            onChange={({ detail }) => setZipCode(detail.value)}
            placeholder="e.g., 1234AB"
          />
        </FormField>

        <FormField label="City">
          <Input
            value={city}
            onChange={({ detail }) => setCity(detail.value)}
            placeholder="e.g., Utrecht"
          />
        </FormField>

        <FormField label="Country">
          <Input
            value={country}
            onChange={({ detail }) => setCountry(detail.value)}
            placeholder="e.g., Nederland"
          />
        </FormField>

        <FormField label="Contact Name">
          <Input
            value={contactName}
            onChange={({ detail }) => setContactName(detail.value)}
            placeholder="Person to contact"
          />
        </FormField>

        <FormField label="Contact Email">
          <Input
            value={contactEmail}
            onChange={({ detail }) => setContactEmail(detail.value)}
            placeholder="info@example.com"
          />
        </FormField>

        <FormField label="Contact Phone">
          <Input
            value={contactPhone}
            onChange={({ detail }) => setContactPhone(detail.value)}
            placeholder="+31 6 1234 5678"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default AddCustomerModal;
